export type EventCircleVisibility =
  | "PRIVATE"
  | "PUBLIC";

export type EventCircleStatus =
  | "ACTIVE"
  | "CANCELLED";

export type EventCircleResponse =
  | "AWAITING"
  | "GOING"
  | "MAYBE"
  | "BUSY"
  | "NOT_GOING";

export type RecipientType =
  | "USERNAME"
  | "PHONE";

/* Kept as an alias so older imports still work. */
export type InvitationRecipientType =
  RecipientType;

export type EventCircle = {
  circleId: string;
  title: string;
  description: string;
  location: string;
  startsAt: number;
  endsAt: number;
  visibility: EventCircleVisibility;
  status: EventCircleStatus;
  editCount: number;
  updatedAt: number;

  isHost: boolean;
  responseStatus: EventCircleResponse;
  shareable: boolean;

  /*
   * Present only immediately after creation or when an
   * invitation link is opened. Never save it in localStorage.
   */
  token?: string;

  /*
   * Lets the backend identify a received invitation without
   * exposing recipient username or WhatsApp number.
   */
  invitationId?: string;
};

export type EventCircleDashboard =
  EventCircle & {
    going: number;
    maybe: number;
    busy: number;
    notGoing: number;
    awaiting: number;
  };

export type CreateEventCircleInput = {
  title: string;
  description: string;
  location: string;
  startsAt: number;
  endsAt: number;
  visibility: EventCircleVisibility;

  /*
   * Required only for PRIVATE invitations.
   * PUBLIC Event Circles do not need a recipient.
   */
  recipientType?: RecipientType;
  recipient?: string;
};

export type UpdateEventCircleInput = {
  circleId: string;
  title: string;
  description: string;
  location: string;
  startsAt: number;
  endsAt: number;
};

export type EventCircleListResponse = {
  circles: EventCircle[];
};

export type EventCircleShareLink = {
  url: string;
  shareable: boolean;
};

export const responseOptions: Array<{
  value: Exclude<
    EventCircleResponse,
    "AWAITING"
  >;
  label: string;
}> = [
  {
    value: "GOING",
    label: "Going",
  },
  {
    value: "MAYBE",
    label: "Maybe",
  },
  {
    value: "BUSY",
    label: "Busy",
  },
  {
    value: "NOT_GOING",
    label: "Not going",
  },
];

export function responseLabel(
  response: EventCircleResponse,
): string {
  switch (response) {
    case "GOING":
      return "Going";

    case "MAYBE":
      return "Maybe";

    case "BUSY":
      return "Busy";

    case "NOT_GOING":
      return "Not going";

    default:
      return "Awaiting response";
  }
}

export function editLabel(
  editCount: number,
): string {
  if (editCount <= 0) {
    return "Original details";
  }

  if (editCount === 1) {
    return "Edited";
  }

  return `${editCount} edits`;
}

export function formatEventDateTime(
  timestamp: number,
): string {
  return new Intl.DateTimeFormat(
    "en-MY",
    {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
  ).format(new Date(timestamp));
}